/**
 * The pictures of a product for the front office (main view, customization
 * views by size display), their position and scale, the master watermark of the
 * line, and the threads (colours) the product can be customized with.
 */
import { useRef, useState, type SubmitEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faCaretUp,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import type { ProductLine } from '@/features/product-lines/api';
import { productLinePictureUrl } from '@/features/product-lines/product-line-form';
import plusIcon from '@/assets/images/plus.svg';
import {
  filterMatrix,
  fontWithWeight,
  getTextZoneStyle,
  hexToRgb,
} from '@/shared/lib/text-zone';
import { Dialog } from '@/shared/ui/Dialog';
import { usePicture } from '@/shared/ui/use-picture';
import {
  deleteProductPicture,
  productQueryOptions,
  threadsQueryOptions,
  updateProduct,
  uploadProductPicture,
  type Product,
} from './api';
import {
  abcToImageProps,
  EMPTY_ABC,
  moveColor,
  removeColor,
  productToAbc,
  specialPositionProps,
  splitThreads,
  unselectedThreads,
  validateAbc,
  VIEWS,
  type AbcValues,
  type Color,
  type SpecialPositionValues,
  type View,
} from './abc-form';
import { productPictureUrl } from './logic';
import { PreviewPictureField, TextZone } from './preview';
import { imageTransform, previewText, useArrowNudge } from './preview-logic';
import { TextSpecialPositioningDialog } from './TextSpecialPositioningDialog';
import { Select } from '@/shared/ui/Select';

export interface ProductAbcDialogProps {
  item: Product;
  lines: readonly ProductLine[];
  /** Closed with `true` when a picture changed, so the list refreshes its thumbnails. */
  onClose: (imagesChanged: boolean) => void;
}

interface Colors {
  iconic: Color[];
  exclusive: Color[];
  current: string;
  specialPosition: Record<string, unknown>;
}

export function ProductAbcDialog({
  item,
  lines,
  onClose,
}: Readonly<ProductAbcDialogProps>) {
  const product = useQuery(productQueryOptions(item.id));
  const { data: threads = [] } = useQuery(threadsQueryOptions());
  const productLine = lines.find(
    (line) => line.productLine === product.data?.line,
  );
  const sizeDisplay = productLine?.sizeDisplay ?? 'noSize';

  // Edits start from the fetched record.
  const [valueEdits, setValueEdits] = useState<AbcValues | null>(null);
  const values =
    valueEdits ?? (product.data ? productToAbc(product.data) : EMPTY_ABC);
  const setValues = (update: (prev: AbcValues) => AbcValues) =>
    setValueEdits(update(values));
  const [colorEdits, setColorEdits] = useState<Colors | null>(null);
  const colors: Colors =
    colorEdits ??
    (() => {
      const { iconic, exclusive } = splitThreads(
        product.data?.textProps?.threads,
      );
      return {
        iconic,
        exclusive,
        current: iconic[0]?.hex ?? exclusive[0]?.hex ?? '#ffffff',
        specialPosition: product.data ? specialPositionProps(product.data) : {},
      };
    })();
  const setColors = (patch: Partial<Colors>) =>
    setColorEdits({ ...colors, ...patch });
  const [disableMasterCustom, setDisableMasterCustom] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [specialOpen, setSpecialOpen] = useState(false);

  const pictures = {
    main: usePicture(),
    custom: usePicture(),
    custom2: usePicture(),
    custom3: usePicture(),
  };
  const rows = {
    main: useRef<HTMLDivElement>(null),
    custom: useRef<HTMLDivElement>(null),
    custom2: useRef<HTMLDivElement>(null),
    custom3: useRef<HTMLDivElement>(null),
  };
  useArrowNudge(rows, (view, dx, dy) =>
    setValues((prev) => ({
      ...prev,
      [view]: {
        ...prev[view],
        x: (Number.isNaN(prev[view].x) ? 0 : prev[view].x) + dx,
        y: (Number.isNaN(prev[view].y) ? 0 : prev[view].y) + dy,
      },
    })),
  );

  const set = (view: View, key: 'scale' | 'x' | 'y', value: number) =>
    setValues((prev) => ({ ...prev, [view]: { ...prev[view], [key]: value } }));
  const errors = submitted ? validateAbc(values) : {};

  const save = useMutation({
    mutationFn: async () => {
      const threadsToSend = [...colors.iconic, ...colors.exclusive];
      try {
        await updateProduct({
          id: item.id,
          mainPictureUpdatedAt: Date.now(),
          customPictureUpdatedAt: Date.now(),
          custom2PictureUpdatedAt: Date.now(),
          custom3PictureUpdatedAt: Date.now(),
          imageProps: abcToImageProps(values),
          textProps: { threads: threadsToSend, ...colors.specialPosition },
        });
      } catch (error) {
        throw new Error('Failed to update product', { cause: error });
      }
      try {
        for (const view of VIEWS) {
          const picture = pictures[view];
          if (!picture.touched) continue;
          if (picture.file)
            await uploadProductPicture(item.id, picture.file, view);
          else await deleteProductPicture(item.id, view);
        }
      } catch (error) {
        throw new Error('Failed to upload product pictures', { cause: error });
      }
      return VIEWS.some((view) => pictures[view].touched);
    },
    onSuccess: (imagesChanged) => onClose(imagesChanged),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    if (Object.keys(validateAbc(values)).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const textureImg = productLinePictureUrl(productLine?.id, 'texture') ?? '';
  const latin = productLine?.keyboard?.latin;
  const text = previewText(latin?.maxChars);
  const textPropsOf = (view: 'custom' | 'custom2') =>
    sizeDisplay === 'shoeSize'
      ? (colors.specialPosition[view] as Record<string, number> | undefined)
      : productLine?.textProps;
  const textZoneStyle = (view: 'custom' | 'custom2') => ({
    ...getTextZoneStyle({
      textProps: textPropsOf(view),
      color: colors.current,
      shadowType: productLine?.shadowType,
      font: fontWithWeight(latin?.font, latin?.fontWeight),
    }),
    backgroundImage: `url(${textureImg})`,
  });
  const imageProps = abcToImageProps(values);
  const masterCustom = productLine?.imageProps?.custom ?? {};

  const numberField = (
    view: View,
    key: 'scale' | 'x' | 'y',
    label: string,
    placeholder: string,
  ) => (
    <div className="mb-2">
      <label className="field-label" htmlFor={`abc-${view}-${key}`}>
        {label}
      </label>
      <input
        id={`abc-${view}-${key}`}
        data-testid={`product-abc-form-${view}-${key}`}
        className="field"
        type="number"
        name={`${view}-${key}`}
        placeholder={placeholder}
        value={Number.isNaN(values[view][key]) ? '' : values[view][key]}
        onChange={(event) => set(view, key, event.target.valueAsNumber)}
      />
      <small
        hidden={!(key === 'scale' && errors[view])}
        data-testid={`product-abc-form-${view}-${key}-error`}
      >
        {errors[view]}
      </small>
    </div>
  );

  const viewColumn = (
    view: View,
    title: string,
    kind: 'main' | 'custom',
    overlay?: React.ReactNode,
    extra?: React.ReactNode,
  ) => (
    <div className="flex-1 px-[15px]">
      {sizeDisplay === 'shoeSize' && view !== 'main' ? (
        <h4>{title}</h4>
      ) : (
        <h3>{title}</h3>
      )}
      {numberField(view, 'scale', 'Scale', 'Scale')}
      {numberField(view, 'y', 'Y', 'Y')}
      {numberField(view, 'x', 'X', 'X')}
      <div ref={rows[view]}>
        <PreviewPictureField
          kind={kind}
          id={`abc-${view}-picture`}
          name={`${view}Picture`}
          label="Picture"
          picture={pictures[view]}
          serverUrl={productPictureUrl(item.id, view)}
          imageStyle={imageTransform(imageProps[view])}
          overlay={overlay}
          testId={`product-abc-form-${view}-picture`}
        />
      </div>
      {extra}
    </div>
  );

  const colorList = (list: Color[], type: 'iconic' | 'exclusive') =>
    list.map((color, index) => (
      <div
        className="color"
        key={color.hex}
        data-testid={`product-abc-form-${type}-thread`}
        data-value={color.hex}
      >
        <div className="flex items-center">
          <div
            className="color-preview"
            style={{ backgroundColor: color.hex }}
          />
          <div>
            {color.name} ({color.hex})
          </div>
        </div>
        <div className="flex items-center">
          <button
            type="button"
            className="color-test-btn"
            onClick={() => setColors({ current: color.hex })}
            data-testid={`product-abc-form-${type}-thread-test-button`}
          >
            test
          </button>
          <button
            type="button"
            className="action-icon"
            aria-label={`Remove ${color.name}`}
            onClick={() => setColors({ [type]: removeColor(list, index) })}
            data-testid={`product-abc-form-${type}-thread-remove-button`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
          <button
            type="button"
            className="action-icon"
            aria-label={`Move ${color.name} up`}
            onClick={() => setColors({ [type]: moveColor(list, index, -1) })}
            data-testid={`product-abc-form-${type}-thread-up-button`}
          >
            <FontAwesomeIcon icon={faCaretUp} />
          </button>
          <button
            type="button"
            className="action-icon"
            aria-label={`Move ${color.name} down`}
            onClick={() => setColors({ [type]: moveColor(list, index, 1) })}
            data-testid={`product-abc-form-${type}-thread-down-button`}
          >
            <FontAwesomeIcon icon={faCaretDown} />
          </button>
        </div>
      </div>
    ));

  const selectable = unselectedThreads(
    threads,
    colors.iconic,
    colors.exclusive,
  );
  const addThread = (hex: string) => {
    const thread = selectable.find((entry) => entry.hex === hex);
    if (!thread) return;
    const color: Color = {
      hex: thread.hex,
      name: thread.name,
      type: thread.type,
    };
    if (color.type === 'iconic')
      setColors({ iconic: [...colors.iconic, color] });
    else setColors({ exclusive: [...colors.exclusive, color] });
  };

  return (
    <Dialog
      title="Product ABC Edit"
      onClose={() => onClose(false)}
      width="1200px"
      testId="product-abc-dialog"
    >
      <svg style={{ display: 'none' }} aria-hidden="true">
        <filter id="textZoneFilter">
          <feColorMatrix
            type="matrix"
            values={filterMatrix(hexToRgb(colors.current) ?? undefined)}
          />
        </filter>
      </svg>
      <form
        className="form popin-form popin-product"
        onSubmit={onSubmit}
        noValidate
      >
        <div className="-mx-[15px] mb-1 flex">
          {viewColumn('main', 'Main view', 'main')}
          {viewColumn(
            'custom',
            sizeDisplay === 'shoeSize'
              ? 'Custom 1 / left shoe'
              : 'Customization view',
            'custom',
            <>
              <TextZone
                style={textZoneStyle('custom')}
                text={text}
                textureImg={textureImg}
                curve={textPropsOf('custom')?.curve}
              />
              {sizeDisplay !== 'shoeSize' && !disableMasterCustom && (
                <img
                  className="custom-filigran"
                  src={
                    productLinePictureUrl(productLine?.id, 'custom') ??
                    undefined
                  }
                  alt=""
                  style={imageTransform(masterCustom)}
                />
              )}
            </>,
            sizeDisplay !== 'shoeSize' && (
              <div className="styled-input-square styled-input-single">
                <input
                  type="checkbox"
                  id="disableMasterCustom"
                  data-testid="product-abc-form-disable-master-custom"
                  name="disableMasterCustom"
                  checked={disableMasterCustom}
                  onChange={(event) =>
                    setDisableMasterCustom(event.target.checked)
                  }
                />
                <label htmlFor="disableMasterCustom">
                  Disable master product line view
                </label>
              </div>
            ),
          )}
          {sizeDisplay !== 'noSize' &&
            viewColumn(
              'custom2',
              sizeDisplay === 'shoeSize'
                ? 'Custom 2 / right shoe'
                : 'Zoom view',
              'custom',
              sizeDisplay === 'shoeSize' && (
                <TextZone
                  style={textZoneStyle('custom2')}
                  text={text}
                  textureImg={textureImg}
                  curve={textPropsOf('custom2')?.curve}
                />
              ),
            )}
          {sizeDisplay === 'shoeSize' &&
            viewColumn('custom3', 'Custom 3 / above view', 'custom')}

          <div className="w-3/12 px-[15px]">
            <h3 className="text-center">Colors</h3>
            <div className="mb-4 mt-6">
              <div className="colors-container">
                {colors.iconic.length > 0 && (
                  <h4 className="text-center">Iconic</h4>
                )}
                {colorList(colors.iconic, 'iconic')}
                {colors.exclusive.length > 0 && (
                  <h4 className="text-spacing text-center">Exclusive</h4>
                )}
                {colorList(colors.exclusive, 'exclusive')}
              </div>
              <div className="field field-selector">
                <Select
                  aria-label="Thread"
                  placeholder="--Select a thread--"
                  options={selectable.map((thread) => ({
                    value: thread.hex,
                    label: thread.label,
                  }))}
                  value=""
                  onChange={addThread}
                  testId="product-abc-form-thread"
                />
              </div>
            </div>
            {sizeDisplay === 'shoeSize' && (
              <button
                type="button"
                className="add-btn mt-4 flex items-center"
                onClick={() => setSpecialOpen(true)}
                data-testid="product-abc-form-special-positioning-button"
              >
                <img src={plusIcon} alt="" />
                <h5 className="pl-2">Special positioning</h5>
              </button>
            )}
          </div>
        </div>
        {errorMessage && (
          <div
            className="alert mb-0 pb-0"
            role="alert"
            data-testid="product-abc-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-2 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            disabled={save.isPending}
            data-testid="product-abc-form-save-button"
          >
            Save
          </button>
        </div>
      </form>
      {specialOpen && product.data && productLine && (
        <TextSpecialPositioningDialog
          product={product.data}
          productLine={productLine}
          textProps={colors.specialPosition}
          imageProps={imageProps}
          currentColor={colors.current}
          onClose={(result: SpecialPositionValues | null) => {
            setSpecialOpen(false);
            if (result) setColors({ specialPosition: result });
          }}
        />
      )}
    </Dialog>
  );
}
